import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  put,
  fork,
  all,
  takeLatest,
  takeEvery,
  select,
} from 'redux-saga/effects';
import {
  createHearingInitialize,
  deleteHearingInitialize,
  removeCommentsFromStore,
  removeContentsFromStore,
  removeUserHearingRolesFromStore,
} from '../actions/api';
import { push } from 'connected-react-router';
import { CREATE_HEARING_URL } from '../../constants/urls';
import { getEntityId, getRelIds } from '../helpers/apiHelpers';
import { getRequestData } from '../helpers/selectorHelpers';
import { awaitApiCall } from '../helpers/sagaHelpers';
import { parseApiError } from '../globalMessageSlice';
import { hearingByIdSelector } from '../apiFeatures/hearingSlice';
import { allCommentsByHearingIdSelector } from '../apiFeatures/commentSlice';
import { downloadFileFromUrlWithApiKey } from '../../utilities/files';
import { baseApiUrlSelector } from '../bootstrapSlice';

// -------- Actions ---------
const createNewHearing = createAction('page/myHearings/createNewHearing');
const deleteHearing = createAction('page/myHearings/deleteHearing');
const openHearing = createAction('page/myHearings/openHearing');
const setIsCreatingHearing = createAction(
  'page/myHearings/setIsCreatingHearing',
);
const setIsDeletingHearing = createAction(
  'page/myHearings/setIsDeletingHearing',
);
const downloadReport = createAction('page/myHearings/downloadReport');
const downloadContentAsZip = createAction(
  'page/myHearings/downloadContentAsZip',
);

// -------- Reducers ---------
const myHearingsPageReducer = createReducer(
  { isCreatingHearing: false, isDeletingHearing: false },
  (builder) => {
    builder
      .addCase(setIsCreatingHearing, (state, action) => {
        state.isCreatingHearing = action.payload;
      })
      .addCase(setIsDeletingHearing, (state, action) => {
        state.isDeletingHearing = action.payload;
      });
  },
);

// -------- Sagas ---------
export function* myHearingsPageSaga() {
  yield all([
    fork(createNewHearingSaga),
    fork(rerouteToCreateHearingPageOnClick),
    fork(deleteHearingSaga),
    fork(downloadReportSaga),
    fork(downloadContentAsZipSaga),
  ]);
}

function* downloadReportSaga() {
  yield takeLatest(downloadReport.type, function* (action) {
    const baseApiUrl = yield select(baseApiUrlSelector);
    const { hearingId, format } = action.payload;

    var url = `${baseApiUrl}/hearing/${hearingId}/export/${format}`;
    return downloadFileFromUrlWithApiKey(url);
  });
}

function* downloadContentAsZipSaga() {
  yield takeLatest(downloadContentAsZip.type, function* (action) {
    const baseApiUrl = yield select(baseApiUrlSelector);
    const { hearingId } = action.payload;

    var url = `${baseApiUrl}/content/${hearingId}/downloadAll`;
    return downloadFileFromUrlWithApiKey(url);
  });
}

function* deleteHearingSaga() {
  yield takeLatest(deleteHearing.type, function* (action) {
    const hearingId = action.payload;
    if (!hearingId) {
      return;
    }

    yield put(setIsDeletingHearing(true));

    const hearing = yield select(hearingByIdSelector(hearingId));
    const comments = yield select(allCommentsByHearingIdSelector(hearingId));

    const userHearingRoleIds = getRelIds(hearing, 'userHearingRoles');
    const contentIds = getRelIds(hearing, 'contents');
    const commentIds = comments.map((comment) => getEntityId(comment));

    const initAction = yield put(deleteHearingInitialize({ hearingId }));
    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(removeContentsFromStore(contentIds));
      yield put(removeUserHearingRolesFromStore(userHearingRoleIds));
      yield put(removeCommentsFromStore(commentIds));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsDeletingHearing(false));
  });
}

function* createNewHearingSaga() {
  yield takeLatest(createNewHearing.type, function* () {
    yield put(setIsCreatingHearing(true));

    const initAction = yield put(createHearingInitialize());
    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      const createdHearing = yield getRequestData(actionResult.success);
      yield put(
        push(CREATE_HEARING_URL, {
          hearingId: getEntityId(createdHearing),
        }),
      );
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsCreatingHearing(false));
  });
}

function* rerouteToCreateHearingPageOnClick() {
  yield takeEvery(openHearing.type, function* (action) {
    const { hearingId, stepNumber } = action.payload;
    if (hearingId) {
      yield put(push(CREATE_HEARING_URL, { hearingId, stepNumber }));
    }
  });
}

// -------- Selectors ---------
const selectMyHearingPageState = (state) => state.page.myHearings;

const isCreatingHearingSelector = createSelector(
  selectMyHearingPageState,
  (state) => state.isCreatingHearing,
);
const isDeletingHearingSelector = createSelector(
  selectMyHearingPageState,
  (state) => state.isDeletingHearing,
);

// -------- Exporting ---------
export {
  createNewHearing,
  deleteHearing,
  openHearing,
  downloadReport,
  downloadContentAsZip,
  isCreatingHearingSelector,
  isDeletingHearingSelector,
};
export default myHearingsPageReducer;
